import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { goBack } from 'react-router-redux'
import { some, find } from 'lodash'
import getCookie from 'utils/cookie'
import {
  saveApplication,
  saveApplicationForLater,
  saveDraft,
  removeDraft,
} from 'careers/actions/application'
import { USE_FILE_UPLOAD } from 'features'
import { browserHistory } from 'react-router'
import { actions } from 'react-redux-form'
import { mapLanguageToComponentProps } from 'careers/i18n'
import { logEvent, EVENT_ACTIONS } from 'actions/audit-trail'
import JobAdQuickInterview from 'components/job-ad-quick-interview'
import { QuestionType, VideoType } from 'types'

interface JobAdQuickInterviewPageProps {
  application: {
    _id: string
    email: string
    videoResponses: VideoType[]
    completed: boolean
  }
  dispatch: any
  jobAd: {
    formSetup: {
      questions: {
        visible: boolean
        required: boolean
      }
    }
    questions: QuestionType[]
  }
  params: {
    adSlug: string
    companySlug: string
    language: string
    token?: string
  }
  company: {
    _id: string
  }
  globalRecorder?: string
}

const mapStateToProps = (state: any) => ({
  application: state.application,
  jobAd: state.jobAd,
  company: state.careers.company,
  globalRecorder: state.global.recorder,
})

export function JobAdQuickInterviewPage({
  application,
  dispatch,
  jobAd,
  params,
  company,
  globalRecorder = '',
}: JobAdQuickInterviewPageProps) {
  const [highlightRequired, setHighlightRequired] = useState<boolean>(false)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)
  const [continueLaterLoading, setContinueLaterLoading] =
    useState<boolean>(false)

  useEffect(() => {
    // when trying to open the quick interview page without existing application,
    // redirect to job-ad details page
    if (!application._id) {
      browserHistory.replace(
        `/careers/${params.language}/${params.companySlug}/${params.adSlug}`
      )
    } else {
      dispatch(
        logEvent({
          ec: company._id,
          ea: EVENT_ACTIONS.jobAdQuickInterviewView,
        })
      )
    }
  }, [])

  const onSubmitApplication = () => {
    const referrer = getCookie('referrer')
    const data = { ...application, completed: true }

    if (
      jobAd.formSetup.questions.required &&
      some(application.videoResponses, { has: false })
    ) {
      setHighlightRequired(true)
      scrollTo(0, 0)
      return undefined
    }

    setSubmitLoading(true)
    return dispatch(saveApplication(data, referrer))
      .then(() => {
        setSubmitLoading(false)
        browserHistory.push(
          `/careers/${params.language}/${params.companySlug}/${params.adSlug}/thankyou`
        )
        dispatch(actions.reset('application'))
        dispatch(actions.change('application.email', application.email))
      })
      .catch(() => setSubmitLoading(false))
  }

  const onBack = () => {
    dispatch(goBack())
  }

  const onVideoChange = (
    questionId: string,
    application: {
      _id: string
      videoResponses: VideoType[]
      completed: boolean
    }
  ) => {
    const videoResponse = find(application.videoResponses, { questionId })

    dispatch(
      saveDraft({
        isMobile: USE_FILE_UPLOAD || globalRecorder === 'upload',
        applicationId: application._id,
        response: videoResponse,
      })
    )
  }

  const onVideoRemove = (questionId: string) => {
    const videoResponse = find(application.videoResponses, { questionId })
    dispatch(
      removeDraft({
        applicationId: application._id,
        response: videoResponse,
      })
    )
  }

  const saveForLater = () => {
    setContinueLaterLoading(true)
    return dispatch(saveApplicationForLater(application))
      .then(() => {
        setContinueLaterLoading(false)
        browserHistory.push(
          `/careers/${params.language}/${params.companySlug}/${params.adSlug}/seeyou`
        )
        dispatch(actions.reset('application'))
      })
      .catch(() => setContinueLaterLoading(false))
  }

  return (
    <JobAdQuickInterview
      onBack={onBack}
      onSubmitApplication={onSubmitApplication}
      saveForLater={saveForLater}
      submitLoading={submitLoading}
      company={company}
      continueLaterLoading={continueLaterLoading}
      onVideoChange={onVideoChange}
      onVideoRemove={onVideoRemove}
      highlightRequired={highlightRequired}
    />
  )
}

export default connect(mapLanguageToComponentProps(mapStateToProps))(
  JobAdQuickInterviewPage
)
